/**
 * @breaker/web-beacon — the ONE first-party analytics beacon (Phase A U1).
 *
 * Ships ONLY on Breaker-identity surfaces: the marketing site (Beacon.astro)
 * and the signup wizard (mounted on the /signup page, torn down on unmount).
 * NEVER on fleet portals, booking pages, tracking pages, or my./admin. hosts
 * — fleet traffic is theirs, not ours (law 2).
 *
 * Privacy (law 1): no cookies, no persistent identifiers, no PII. The
 * session id lives in sessionStorage only (single-visit stitching); the
 * referrer is reduced to an external hostname; everything else the server
 * derives (and the visitor hash it computes rotates daily).
 *
 * Behaviour: pageview on init; heartbeat every 15s while the document is
 * visible (dwell = sum at query time); pagehide flush via sendBeacon;
 * click events on [data-beacon] (legacy attribute, kept so no page needed
 * retagging) and [data-track]; the wizard emits wizard_step via event().
 * Events batch through one queue — posts go same-origin to /_b, which Caddy
 * rewrites to the ingest endpoint. Failures are silent: analytics must
 * never cost a visitor anything.
 */
const HEARTBEAT_MS = 15_000;
const FLUSH_DEBOUNCE_MS = 1_000;
function sessionId() {
    const KEY = 'bk_sid';
    try {
        const existing = sessionStorage.getItem(KEY);
        if (existing && /^[a-f0-9]{16,32}$/.test(existing))
            return existing;
    }
    catch {
        /* storage unavailable — fall through to a per-load id */
    }
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    const sid = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    try {
        sessionStorage.setItem(KEY, sid);
    }
    catch {
        /* per-load id it is */
    }
    return sid;
}
function externalReferrer(internalSuffix) {
    try {
        if (!document.referrer)
            return null;
        const host = new URL(document.referrer).hostname;
        if (!host || host === location.hostname || host.endsWith(internalSuffix))
            return null;
        return host;
    }
    catch {
        return null;
    }
}
export function initBeacon(opts = {}) {
    const noop = { event: () => undefined, teardown: () => undefined };
    try {
        const endpoint = opts.endpoint ?? '/_b';
        const internalSuffix = opts.internalReferrerSuffix ?? location.hostname;
        const sid = sessionId();
        const referrer = externalReferrer(internalSuffix);
        let queue = [];
        let flushTimer = null;
        function post(body) {
            try {
                // text/plain keeps this a "simple request" even if it ever crosses
                // origins; the ingest endpoint parses JSON out of the text body.
                const blob = new Blob([body], { type: 'text/plain' });
                if (!navigator.sendBeacon?.(endpoint, blob)) {
                    void fetch(endpoint, {
                        method: 'POST',
                        headers: { 'content-type': 'text/plain' },
                        body,
                        keepalive: true,
                    }).catch(() => undefined);
                }
            }
            catch {
                /* never surface */
            }
        }
        function flush() {
            if (flushTimer) {
                clearTimeout(flushTimer);
                flushTimer = null;
            }
            if (queue.length === 0)
                return;
            const events = queue;
            queue = [];
            post(JSON.stringify({ sid, events }));
        }
        function enqueue(e) {
            queue.push(e);
            if (!flushTimer)
                flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
        }
        // ---- pageview (with the environment props the dashboard filters on) ----
        enqueue({
            type: 'pageview',
            path: location.pathname,
            ...(referrer ? { referrer } : {}),
            props: {
                webdriver: navigator.webdriver === true,
                language: navigator.language ?? '',
                viewport: `${window.innerWidth}x${window.innerHeight}`,
            },
        });
        // ---- heartbeat while visible (dwell = sum of beats at query time) ----
        const heartbeat = setInterval(() => {
            if (document.visibilityState === 'visible') {
                enqueue({ type: 'heartbeat', path: location.pathname });
            }
        }, HEARTBEAT_MS);
        // ---- clicks on tagged elements ----
        const onClick = (e) => {
            try {
                const el = e.target?.closest?.('[data-beacon],[data-track]');
                if (!el)
                    return;
                const beaconLabel = el.getAttribute('data-beacon');
                const label = beaconLabel ?? el.getAttribute('data-track');
                if (!label)
                    return;
                enqueue({
                    type: 'click',
                    path: location.pathname,
                    props: { target: label, src: beaconLabel !== null ? 'beacon' : 'track' },
                });
            }
            catch {
                /* never surface */
            }
        };
        document.addEventListener('click', onClick);
        // ---- flush on leave (sendBeacon survives the unload) ----
        const onPagehide = () => flush();
        const onVisibility = () => {
            if (document.visibilityState === 'hidden')
                flush();
        };
        window.addEventListener('pagehide', onPagehide);
        document.addEventListener('visibilitychange', onVisibility);
        return {
            event(type, props) {
                try {
                    enqueue({ type, path: location.pathname, ...(props ? { props } : {}) });
                }
                catch {
                    /* never surface */
                }
            },
            teardown() {
                try {
                    flush();
                    clearInterval(heartbeat);
                    document.removeEventListener('click', onClick);
                    window.removeEventListener('pagehide', onPagehide);
                    document.removeEventListener('visibilitychange', onVisibility);
                }
                catch {
                    /* never surface */
                }
            },
        };
    }
    catch {
        return noop;
    }
}
//# sourceMappingURL=index.js.map